import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import Switch from '@mui/material/Switch';

import { useAccountSearchAllQuery } from "@query/accountQuery";
import { useSendPointsToAccountMutation } from "@mutation/clubMutation";
import { useAccountMemberUpdateMutation } from "@mutation/accountMutation";
import { AccountContext } from "@context/AccountProvider";
import { AlertContext } from "@context/AlertProvider";
import ImageView from "@component/ImageView";

export default function UserManagementPage() {
  const { t } = useTranslation(["main", "userManagement"]);
  const { club } = useContext(AccountContext);
  const { addAlertBanner } = useContext(AlertContext);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [pointsAmount, setPointsAmount] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const accountSearchQuery = useAccountSearchAllQuery({
    page: paginationModel.page,
    search: debouncedSearch,
    pageSize: paginationModel.pageSize,
  });

  const sendPointsMutation = useSendPointsToAccountMutation();
  const accountMemberUpdateMutation = useAccountMemberUpdateMutation();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 400);

    return () => clearTimeout(handler);
  }, [searchText]);

  const perPage = accountSearchQuery.data?.data?.pagination?.per_page;
  useEffect(() => {
    if (perPage && perPage !== paginationModel.pageSize) {
      setPaginationModel((prev) => ({ ...prev, pageSize: perPage }));
    }
  }, [perPage, paginationModel.pageSize]);

  useEffect(() => {
    if (accountSearchQuery.isError) {
      const message =
        accountSearchQuery.error?.response?.data?.detail?.[0]?.msg ||
        accountSearchQuery.error?.message ||
        t("userManagement:searchError");
      addAlertBanner(message, "error");
    }
  }, [accountSearchQuery.error, accountSearchQuery.isError, addAlertBanner, t]);

  const rows = useMemo(() => {
    return (
      accountSearchQuery.data?.data?.items?.map((item) => ({
        ...item,
        file_image_uuid: item.file_image_uuid || item.file_image?.uuid,
      })) || []
    );
  }, [accountSearchQuery.data]);

  const totalItems = accountSearchQuery.data?.data?.pagination?.total_items;
  const rowCountRef = useRef(0);
  const rowCount = useMemo(() => {
    if (typeof totalItems === "number") {
      rowCountRef.current = totalItems;
    }
    return rowCountRef.current;
  }, [totalItems]);

  const toggleLoyaltyMember = (value, account) =>{

    if (!typeof value === "boolean"){
      return;
    }

    accountMemberUpdateMutation.mutate(
      {
        data: {
          account_uuid: account.uuid,
          is_loyalty_member: value
        },
      },
      {
        onSuccess: () => {
          addAlertBanner(
            t("userManagement:toggleLoyaltyMemberSucess", {
              name: `${account.first_name} ${account.last_name}`,
            }),
            "success"
          );
          handleCloseModal();
          accountSearchQuery.refetch();
        },
        onError: (error) => {
          const message =
            error?.response?.data?.detail?.[0]?.msg ||
            error?.message ||
            t("main:saveError");
          addAlertBanner(message, "error");
        },
      }
    );
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleOpenModal = (account) => {
    setSelectedAccount(account);
    setPointsAmount("");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setPointsAmount("");
    setSelectedAccount(null);
  };

  const handleConfirmSend = () => {
    if (!selectedAccount || !club?.uuid) {
      addAlertBanner(t("userManagement:errorMessage"), "error");
      return;
    }

    const numericAmount = Number(pointsAmount);
    if (!numericAmount || numericAmount <= 0) {
      addAlertBanner(t("userManagement:invalidAmount"), "error");
      return;
    }

    sendPointsMutation.mutate(
      {
        data: {
          club_uuid: club.uuid,
          account_uuid: selectedAccount.uuid,
          amount: numericAmount,
        },
      },
      {
        onSuccess: () => {
          addAlertBanner(
            t("userManagement:successMessage", {
              name: `${selectedAccount.first_name} ${selectedAccount.last_name}`,
            }),
            "success"
          );
          handleCloseModal();
          accountSearchQuery.refetch();
        },
        onError: (error) => {
          const message =
            error?.response?.data?.detail?.[0]?.msg ||
            error?.message ||
            t("userManagement:errorMessage");
          addAlertBanner(message, "error");
        },
      }
    );
  };

  const columns = [
    {
      field: "avatar",
      headerName: "",
      width: 90,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params?.row || {};
        const fileUuid = row.file_image_uuid || row.file_image?.uuid;
        const initials = `${row.first_name?.[0] || ""}${row.last_name?.[0] || ""}`.toUpperCase();
        const isOnline = Boolean(row.is_online);

        const avatarContent = !fileUuid ? (
          <Avatar sx={{ width: 40, height: 40 }}>{initials || "?"}</Avatar>
        ) : (
          <ImageView
            fileUuid={fileUuid}
            imageSx={{ width: 40, height: 40, borderRadius: "50%" }}
            renderCustom={({ fileUrl }) => (
              <Avatar
                src={fileUrl}
                alt={`${row.first_name || ""} ${row.last_name || ""}`}
                sx={{ width: 40, height: 40 }}
              />
            )}
          />
        );

        const avatarWithStatus = (
          <Box sx={{ position: "relative", width: 40, height: 40 }}>
            {avatarContent}
            {isOnline && (
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  border: (theme) => `2px solid ${theme.palette.background.paper}`,
                }}
              />
            )}
          </Box>
        );

        if (isOnline) {
          return <Tooltip title={t("userManagement:onlineStatus")}>{avatarWithStatus}</Tooltip>;
        }

        return avatarWithStatus;
      },
    },
    {
      field: "fullName",
      headerName: t("name"),
      flex: 1,
      minWidth: 180,
      sortable: false,
      valueGetter: (params) => {
        const row = params?.row || {};
        return `${row.first_name || ""} ${row.last_name || ""}`.trim();
      },
      renderCell: (params) => {
        const row = params?.row || {};
        const value = `${row.first_name || ""} ${row.last_name || ""}`.trim();
        return (
          <Typography variant="body2" noWrap>
            {value || "-"}
          </Typography>
        );
      },
    },
    
    {
      field: "wallet",
      headerName: t("userManagement:wallet"),
      width: 130,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderHeader: () => (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%" }}
        >
          <AccountBalanceWalletRoundedIcon
            fontSize="small"
            sx={{ color: (theme) => (theme.vars || theme).palette.primary.main }}
          />
          <Typography variant="body2">{t("userManagement:wallet")}</Typography>
        </Stack>
      ),
      renderCell: (params) => {
        const balance = params?.row?.balance ?? 0;
        return (
          <Tooltip title={`${t("userManagement:walletTooltip")}: ${balance}`}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" noWrap>
                {balance}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "isLoyaltyMember",
      headerName: t("userManagement:rewarded"),
      width: 240,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title={t("userManagement:loyaltyMemberTooltip")}>
                <Switch
                    checked={params.row.is_loyalty_member}
                    onChange={
                        () => toggleLoyaltyMember(!params.row.is_loyalty_member, params.row)
                    }
                />
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: t("action"),
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title={t("userManagement:sendActionLabel")}>
          <IconButton
            aria-label={t("userManagement:sendActionLabel")}
            color="primary"
            onClick={() => handleOpenModal(params.row)}
            disabled={sendPointsMutation.isLoading}
          >
            <SendRoundedIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", md: "1400px" },
        mx: "auto",
        pb: 4,
        px: { xs: 1, md: 0 },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
        <Typography variant="h2">{t("userManagement:title")}</Typography>
        <Typography variant="body1" color="text.secondary">
          {t("userManagement:subtitle")}
        </Typography>
      </Box>

      <Stack spacing={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          value={searchText}
          onChange={handleSearchChange}
          placeholder={t("userManagement:searchPlaceholder")}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.uuid}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        loading={accountSearchQuery.isFetching}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[paginationModel.pageSize]}
        sx={{
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
      />

      <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>{t("userManagement:modalTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("userManagement:modalDescription", {
              name: selectedAccount
                ? `${selectedAccount.first_name} ${selectedAccount.last_name}`
                : "",
            })}
          </DialogContentText>
          <TextField
            fullWidth
            type="number"
            label={t("userManagement:amountLabel")}
            value={pointsAmount}
            onChange={(event) => setPointsAmount(event.target.value)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirmSend}
            variant="contained"
            disabled={sendPointsMutation.isLoading}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
